import type {
  HttpMethod,
  ProjectDocument,
  RestAction,
} from '../../types/project';

export const HTTP_METHODS: readonly HttpMethod[] = [
  'GET',
  'POST',
  'PUT',
  'PATCH',
  'DELETE',
];

export type ActionEditorIssue = {
  field: string;
  severity: 'warn' | 'info';
  message: string;
};

export type ActionReference = {
  kind: 'componentEvent' | 'pageEvent' | 'binding';
  label: string;
};

export type KeyValueRow = {
  id: number;
  key: string;
  value: string;
};

const ACTION_ID_PREFIX = 'action_rest_';

export function nextRestActionId(
  actions: RestAction[],
  reservedActionIds: string[] = [],
): string {
  const used = new Set([
    ...actions.map((action) => action.id),
    ...reservedActionIds,
  ]);
  let suffix = 1;
  while (used.has(`${ACTION_ID_PREFIX}${suffix}`)) suffix += 1;
  return `${ACTION_ID_PREFIX}${suffix}`;
}

export function createRestAction(
  actions: RestAction[],
  reservedActionIds: string[] = [],
): RestAction {
  return {
    id: nextRestActionId(actions, reservedActionIds),
    name: 'New REST Action',
    description: '',
    method: 'GET',
    url: 'https://api.example.com',
    headers: { Accept: 'application/json' },
    queryParameters: {},
    pathParameters: {},
    bodyTemplate: '',
    authenticationType: 'anonymous',
  };
}

export function duplicateRestAction(
  action: RestAction,
  actions: RestAction[],
  reservedActionIds: string[] = [],
): RestAction {
  const duplicate: RestAction = {
    ...action,
    id: nextRestActionId(actions, reservedActionIds),
    name: `${action.name} Copy`,
    authenticationType: 'anonymous',
    headers: Object.fromEntries(
      Object.entries(action.headers ?? {}).filter(
        ([key]) => !isCredentialHeaderName(key),
      ),
    ),
    queryParameters: { ...(action.queryParameters ?? {}) },
    pathParameters: { ...(action.pathParameters ?? {}) },
  };

  // New definitions must use top-level project.bindings, never the deprecated
  // action-local responseMapping field.
  delete duplicate.responseMapping;
  return duplicate;
}

export function appendRestAction(
  doc: ProjectDocument,
  action: RestAction,
): ProjectDocument {
  return {
    ...doc,
    project: {
      ...doc.project,
      actions: [...doc.project.actions, action],
    },
  };
}

export function replaceRestActionAt(
  doc: ProjectDocument,
  actionIndex: number,
  action: RestAction,
): ProjectDocument {
  if (actionIndex < 0 || actionIndex >= doc.project.actions.length) {
    return doc;
  }

  return {
    ...doc,
    project: {
      ...doc.project,
      actions: doc.project.actions.map((candidate, index) =>
        index === actionIndex ? action : candidate,
      ),
    },
  };
}

export function removeRestActionAt(
  doc: ProjectDocument,
  actionIndex: number,
): ProjectDocument {
  if (actionIndex < 0 || actionIndex >= doc.project.actions.length) {
    return doc;
  }

  return {
    ...doc,
    project: {
      ...doc.project,
      actions: doc.project.actions.filter((_, index) => index !== actionIndex),
    },
  };
}

function bindingReferencesAction(source: string, actionId: string): boolean {
  const responseRoot = `actions.${actionId}.response`;
  return source === responseRoot || source.startsWith(`${responseRoot}.`);
}

export function collectReferencedActionIds(doc: ProjectDocument): string[] {
  const referencedIds = new Set<string>();

  for (const page of doc.project.pages) {
    for (const event of page.events ?? []) {
      if (event.actionId) referencedIds.add(event.actionId);
    }
    for (const component of page.components) {
      for (const event of component.events ?? []) {
        if (event.actionId) referencedIds.add(event.actionId);
      }
    }
  }

  for (const binding of doc.project.bindings) {
    const match = /^actions\.([^.]+)\.response(?:\.|$)/.exec(binding.source);
    if (match) referencedIds.add(match[1]);
  }

  return [...referencedIds];
}

export function findActionReferences(
  doc: ProjectDocument,
  actionId: string,
): ActionReference[] {
  const references: ActionReference[] = [];

  for (const page of doc.project.pages) {
    for (const event of page.events ?? []) {
      if (event.actionId === actionId) {
        references.push({
          kind: 'pageEvent',
          label: `Page "${page.name}" ${event.event}`,
        });
      }
    }

    for (const component of page.components) {
      for (const event of component.events ?? []) {
        if (event.actionId === actionId) {
          references.push({
            kind: 'componentEvent',
            label: `Component "${component.name}" ${event.event}`,
          });
        }
      }
    }
  }

  for (const binding of doc.project.bindings) {
    if (bindingReferencesAction(binding.source, actionId)) {
      references.push({
        kind: 'binding',
        label: `Binding "${binding.id}"`,
      });
    }
  }

  return references;
}

export function buildDeleteConfirmation(
  action: RestAction,
  references: ActionReference[],
): string {
  if (references.length === 0) {
    return `Delete REST action "${action.name}"? This cannot be undone.`;
  }

  const referenceList = references.map((reference) => `- ${reference.label}`).join('\n');
  return (
    `REST action "${action.name}" is referenced by ${references.length} ` +
    `configuration item${references.length === 1 ? '' : 's'}:\n\n` +
    `${referenceList}\n\nDeleting it may leave dangling or ambiguous references. ` +
    `Review the affected events and bindings after deletion. Continue?`
  );
}

const HTTP_HEADER_NAME_PATTERN = /^[!#$%&'*+\-.^_`|~0-9A-Za-z]+$/;
const PATH_PARAMETER_NAME_PATTERN = /^\w+$/;
const CREDENTIAL_HEADER_NAMES = new Set([
  'authorization',
  'proxy-authorization',
  'x-api-key',
  'api-key',
  'x-auth-token',
]);

function isCredentialHeaderName(key: string): boolean {
  return CREDENTIAL_HEADER_NAMES.has(key.toLowerCase());
}

export type RequestMapKeyKind = 'generic' | 'header' | 'path';

function requestMapKeyError(
  key: string,
  keyKind: RequestMapKeyKind,
): string | null {
  if (key !== key.trim()) {
    return 'Keys cannot begin or end with whitespace.';
  }
  if (keyKind === 'header' && isCredentialHeaderName(key)) {
    return 'Credential headers are configured through secure authentication in Slice 3.';
  }
  if (keyKind === 'header' && !HTTP_HEADER_NAME_PATTERN.test(key)) {
    return 'Header names may contain only HTTP token characters.';
  }
  if (keyKind === 'path' && !PATH_PARAMETER_NAME_PATTERN.test(key)) {
    return 'Path parameter names may contain only letters, numbers, and underscores.';
  }
  return null;
}

function validateStringRecord(
  field: string,
  value: Record<string, string> | undefined,
): ActionEditorIssue[] {
  if (!value) return [];
  const issues: ActionEditorIssue[] = [];
  const keyKind: RequestMapKeyKind =
    field === 'Headers'
      ? 'header'
      : field === 'Path parameters'
        ? 'path'
        : 'generic';

  for (const [key, entryValue] of Object.entries(value)) {
    if (!key.trim()) {
      issues.push({
        field,
        severity: 'warn',
        message: `${field} contains an empty key.`,
      });
    } else {
      const keyError = requestMapKeyError(key, keyKind);
      if (keyError) {
        issues.push({
          field,
          severity: 'warn',
          message: `${field} key "${key}" is invalid. ${keyError}`,
        });
      }
    }
    if (typeof entryValue !== 'string') {
      issues.push({
        field,
        severity: 'warn',
        message: `${field} value for "${key}" must be a string.`,
      });
    }
  }

  return issues;
}

export function isAbsoluteHttpUrl(value: string): boolean {
  if (/\s/.test(value)) return false;

  try {
    const url = new URL(value);
    return (
      (url.protocol === 'http:' || url.protocol === 'https:') &&
      url.hostname.length > 0 &&
      url.username.length === 0 &&
      url.password.length === 0
    );
  } catch {
    return false;
  }
}

export function validateRestAction(
  action: RestAction,
  actions: RestAction[],
): ActionEditorIssue[] {
  const issues: ActionEditorIssue[] = [];

  if (!action.id.trim()) {
    issues.push({ field: 'id', severity: 'warn', message: 'Action ID is required.' });
  } else if (!/^[A-Za-z0-9_-]+$/.test(action.id)) {
    issues.push({
      field: 'id',
      severity: 'warn',
      message: 'Action ID may contain only letters, numbers, underscores, and hyphens.',
    });
  }

  if (actions.filter((candidate) => candidate.id === action.id).length > 1) {
    issues.push({
      field: 'id',
      severity: 'warn',
      message: `Action ID "${action.id}" is duplicated.`,
    });
  }

  if (!action.name.trim()) {
    issues.push({ field: 'name', severity: 'warn', message: 'Action name is required.' });
  }

  if (!action.url.trim()) {
    issues.push({ field: 'url', severity: 'warn', message: 'Endpoint URL is required.' });
  } else if (!isAbsoluteHttpUrl(action.url)) {
    issues.push({
      field: 'url',
      severity: 'warn',
      message: 'Endpoint URL must be absolute HTTP or HTTPS without spaces or embedded credentials.',
    });
  }

  if (!HTTP_METHODS.includes(action.method)) {
    issues.push({
      field: 'method',
      severity: 'warn',
      message: `HTTP method "${action.method}" is not supported.`,
    });
  }

  issues.push(...validateStringRecord('Headers', action.headers));
  issues.push(...validateStringRecord('Query parameters', action.queryParameters));
  issues.push(...validateStringRecord('Path parameters', action.pathParameters));
  for (const [key, value] of Object.entries(action.pathParameters ?? {})) {
    if (value.includes('{{')) {
      issues.push({
        field: 'Path parameters',
        severity: 'warn',
        message: `Path parameter "${key}" must use a static replacement value; templates are not supported yet.`,
      });
    }
  }

  if (
    (action.method === 'GET' || action.method === 'DELETE') &&
    action.bodyTemplate?.trim()
  ) {
    issues.push({
      field: 'bodyTemplate',
      severity: 'info',
      message: `${action.method} requests do not send the configured request body.`,
    });
  }

  if (action.authenticationType !== 'anonymous') {
    issues.push({
      field: 'secretReferenceId',
      severity: action.secretReferenceId?.trim() ? 'info' : 'warn',
      message: action.secretReferenceId?.trim()
        ? 'Credential material is resolved and injected by the backend at execution time.'
        : 'Select or enter a server-side secret reference before executing this action.',
    });
  }

  return issues;
}

export function validateKeyValueRows(
  rows: KeyValueRow[],
  keyKind: RequestMapKeyKind = 'generic',
): string | null {
  if (rows.some((row) => row.key.trim().length === 0)) {
    return 'Every row needs a key before it can be added to the project document.';
  }

  for (const row of rows) {
    const keyError = requestMapKeyError(row.key, keyKind);
    if (keyError) return keyError;
  }

  const comparableKeys = rows.map((row) =>
    keyKind === 'header' ? row.key.toLowerCase() : row.key,
  );
  const duplicateIndex = comparableKeys.findIndex(
    (key, index) => comparableKeys.indexOf(key) !== index,
  );
  if (duplicateIndex >= 0) {
    return `Duplicate key "${rows[duplicateIndex].key}".`;
  }

  return null;
}

export function keyValueRowsToRecord(
  rows: KeyValueRow[],
): Record<string, string> {
  return Object.fromEntries(rows.map((row) => [row.key, row.value]));
}
